import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Appointment,
  createInvoiceForAppointment,
  Patient,
} from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type IInvoice,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asDocRef,
  DocumentReference,
  filterUndefined,
  Firestore,
  isWithRef,
  serialise$,
  type SerialisedData,
  unserialise$,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { InvoiceActions } from '../actions';
import { loadDraftInvoiceSuccess } from '../actions/invoices.actions';
import { PatientsFacade } from '../facades/patients.facade';

@Injectable()
export class InvoicesEffects {
  private _actions$ = inject(Actions);
  private _patientsFacade = inject(PatientsFacade);
  loadInvoices$: Observable<Action> = createEffect(() => this._loadInvoices$());
  loadDraftInvoice$: Observable<Action> = createEffect(() =>
    this._loadDraftInvoice$()
  );

  _loadInvoices$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(InvoiceActions.loadInvoices),
      withLatestFrom(this._patientsFacade.currentPatient$),
      switchMap(([_action, patient]) => {
        if (!patient || !isWithRef(patient)) {
          return [];
        }
        return Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          Patient.invoices$
        );
      }),
      serialise$(),
      map((invoices: SerialisedData<WithRef<IInvoice>[]>) =>
        InvoiceActions.loadInvoiceSuccess({ invoices })
      )
    );
  }

  _loadDraftInvoice$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(InvoiceActions.loadDraftInvoice),
      map((action) => ({
        appointment: action.appointment,
        taxRate: action.taxRate,
      })),
      unserialise$(),
      concatMap(async ({ appointment, taxRate }) => {
        const appointmentRef = asDocRef<IAppointment>(appointment.ref);
        const latestAppointment = await Firestore.getDoc(appointmentRef);
        return this._getAppointmentInvoice(latestAppointment, taxRate);
      }),
      filterUndefined(),
      switchMap((invoiceRef) => Firestore.doc$<IInvoice>(invoiceRef)),
      map((invoice) => invoice.reference),
      map((invoiceRef) => loadDraftInvoiceSuccess({ invoiceRef }))
    );
  }

  private async _getAppointmentInvoice(
    appointment: WithRef<IAppointment>,
    taxRate: number
  ): Promise<DocumentReference<IInvoice> | undefined> {
    if (appointment.invoiceRef) {
      return appointment.invoiceRef;
    }

    if (Appointment.hasUnlinkedMigratedInvoice(appointment)) {
      // eslint-disable-next-line no-console
      console.log(
        `Skipping invoice creation for migrated appointment ${appointment.ref.id}`
      );
      return;
    }

    return createInvoiceForAppointment(appointment.ref, taxRate);
  }
}
